/**
 * The morning compose — the board's one sanctioned non-completion write
 * (write-door §5.2). Pick today's plan from the day view's shelf: carried-over
 * offers, today's calendar, due reminders, active Goals' next actions, today's
 * habits, plus ad-hoc entries. Order of picking = position; the board never
 * re-sorts afterwards.
 *
 * Sit-down surfaces only (surfaces ticket 04): the phone cut never opens this.
 */

import type { BoardStateData, ComposeItemInput } from '../types';
import type { UiState } from '../store';
import { isScheduled } from './folds';
import { esc, hhmm } from './util';

export interface ComposeOptions {
  board: BoardStateData;
  ui: UiState;
}

export function renderCompose(o: ComposeOptions): string {
  const { board, ui } = o;
  const picked = new Set(ui.picks.map(pickKey));
  const offers = board.state.data?.composeOffers ?? [];

  const sections: string[] = [];

  if (offers.length) {
    sections.push(
      section(
        'Carried over',
        offers.map((off) =>
          shelfRow(
            {
              label: off.label,
              sourceKind: off.sourceKind,
              sourceRef: off.sourceRef,
              directionId: off.directionId,
              carriedFrom: off.itemId,
            },
            picked,
            off.declineCount > 0 ? `declined ${off.declineCount}×` : '',
          ),
        ),
        'Not re-picking these records a decline — three in a row lands on the weekly review.',
      ),
    );
  }

  sections.push(
    section(
      'Calendar',
      (board.calendar.data.events ?? [])
        .slice()
        .sort((a, b) => Date.parse(a.start) - Date.parse(b.start))
        .map((e) =>
          shelfRow(
            { label: e.title, sourceKind: 'calendar', sourceRef: e.id },
            picked,
            e.allDay ? 'all day' : hhmm(e.start),
          ),
        ),
    ),
  );

  sections.push(
    section(
      'Due',
      (board.reminders.data.due ?? [])
        .filter((r) => !r.done)
        .map((r) => shelfRow({ label: r.title, sourceKind: 'reminder', sourceRef: r.id }, picked, 'clickup')),
    ),
  );

  const dirs = new Map(board.defs.data.directions.map((d) => [d.id, d]));
  sections.push(
    section(
      'Next actions',
      board.defs.data.goals
        .filter((g) => g.status === 'active' && g.nextAction)
        .map((g) =>
          shelfRow(
            { label: g.nextAction as string, sourceKind: 'next_action', sourceRef: g.id },
            picked,
            dirs.get(g.directionId)?.heading ?? g.directionId,
          ),
        ),
    ),
  );

  sections.push(
    section(
      'Habits',
      board.defs.data.habits
        .filter((h) => isScheduled(h, board.today))
        .map((h) => shelfRow({ label: h.name, sourceKind: 'habit', sourceRef: h.id }, picked, 'today')),
    ),
  );

  const pending = ui.pending.has('compose');

  return `<div class="compose-scrim">
    <div class="compose" role="dialog" aria-label="Compose today">
      <div class="compose-head">
        <div>
          <div class="k">Daily review</div>
          <h2>Compose today</h2>
          <p class="meta2">Pick from the shelf. Order here is the order the Spotlight walks — it never re-sorts.</p>
        </div>
        <button class="ghost" data-action="close-compose">Close</button>
      </div>
      <div class="compose-body">
        <div class="shelf-col">${sections.join('')}</div>
        <div class="picks-col">
          <div class="subhead">Today · ${ui.picks.length} picked</div>
          ${
            ui.picks.length
              ? ui.picks
                  .map(
                    (p, i) => `<div class="pickrow">
                      <span class="pos num">${i + 1}</span>
                      <span class="name">${esc(p.label)}</span>
                      <button class="ghost sm" data-pick-move="${i}" data-delta="-1" title="up">↑</button>
                      <button class="ghost sm" data-pick-move="${i}" data-delta="1" title="down">↓</button>
                      <button class="ghost sm" data-pick-remove="${i}" title="remove">×</button>
                    </div>`,
                  )
                  .join('')
              : `<div class="empty">Nothing picked yet. An empty day still counts as composed — the stamp is the review, not the item count.</div>`
          }
          <form class="adhoc" data-adhoc>
            <input name="label" placeholder="Ad-hoc item…" autocomplete="off" />
            <select name="directionId">
              <option value="">no direction</option>
              ${board.defs.data.directions
                .map((d) => `<option value="${esc(d.id)}">${esc(d.heading)}</option>`)
                .join('')}
            </select>
            <button class="ghost" type="submit">Add</button>
          </form>
          <button class="cta" data-action="submit-compose" ${pending ? 'disabled' : ''}>
            ${pending ? 'Composing…' : `Compose ${ui.picks.length} item${ui.picks.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  </div>`;
}

function section(title: string, rows: string[], note?: string): string {
  return `<div class="shelf-section">
    <div class="subhead">${esc(title)}</div>
    ${note ? `<div class="meta2 note">${esc(note)}</div>` : ''}
    ${rows.length ? rows.join('') : `<div class="empty">—</div>`}
  </div>`;
}

export function pickKey(p: ComposeItemInput): string {
  return `${p.sourceKind}|${p.sourceRef ?? ''}|${p.label}`;
}

function shelfRow(item: ComposeItemInput, picked: Set<string>, meta: string): string {
  const on = picked.has(pickKey(item));
  return `<button class="shelfrow ${on ? 'picked' : ''}" data-pick='${esc(JSON.stringify(item))}'>
    <span class="mark">${on ? '✓' : '+'}</span>
    <span class="name">${esc(item.label)}</span>
    ${meta ? `<span class="r">${esc(meta)}</span>` : ''}
  </button>`;
}
